import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, ShieldCheck, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

const STATUS_META = {
  ACTIVE: { label: "Súhlas je aktívny", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-100" },
  EXPIRING_SOON: { label: "Súhlas čoskoro vyprší", icon: AlertTriangle, cls: "text-amber-700 bg-amber-50 border-amber-100" },
  EXPIRED: { label: "Súhlas expiroval", icon: Clock, cls: "text-slate-600 bg-slate-100 border-slate-200" },
  REVOKED: { label: "Súhlas bol odvolaný", icon: XCircle, cls: "text-red-600 bg-red-50 border-red-100" },
};

export default function Consent() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/public/consent/${encodeURIComponent(token)}`)
    .then((r) => setData(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const grant = async () => {
    setBusy(true);
    try {
      await api.post(`/public/consent/${encodeURIComponent(token)}/grant`);
      toast.success("Súhlas bol udelený. Ďakujeme.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/public/consent/${encodeURIComponent(token)}/revoke`);
      toast.success("Súhlas bol odvolaný.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (err) return (
    <Shell>
      <div className="rounded-2xl bg-slate-100 border border-slate-200 p-6 text-center" data-testid="consent-invalid">
        <Clock className="mx-auto text-slate-400 mb-3" size={26} />
        <h1 className="font-head text-lg font-bold text-slate-800">Odkaz je neplatný alebo vypršal</h1>
        <p className="text-slate-500 mt-1 text-sm">Požiadajte spoločnosť o nový odkaz.</p>
      </div>
    </Shell>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-700" /></div>;

  const meta = STATUS_META[data.status];
  const canGrant = ["PENDING", "NONE", "EXPIRED", "REVOKED"].includes(data.status);
  const canRevoke = data.status === "ACTIVE" || data.status === "EXPIRING_SOON";
  const fmt = (d) => d ? new Date(d).toLocaleDateString("sk-SK") : "—";

  return (
    <Shell>
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6" data-testid="consent-page">
        <h1 className="font-head text-xl font-bold text-slate-900">Súhlas s uchovaním v databáze kandidátov</h1>
        <p className="text-sm text-slate-500 mt-1">Spoločnosť <strong>{data.company_name}</strong></p>

        {meta && (
          <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${meta.cls}`} data-testid="consent-status">
            <meta.icon size={15} /> {meta.label}
          </div>
        )}

        <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-700 whitespace-pre-line" data-testid="consent-text">
          {data.consent_text}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <Info label="Účel" value={data.purpose || "Budúce pracovné príležitosti"} />
          <Info label="Doba platnosti" value="2 roky" />
          {data.granted_at && <Info label="Udelený" value={fmt(data.granted_at)} />}
          {data.expires_at && <Info label="Platný do" value={fmt(data.expires_at)} />}
        </div>

        {canGrant && (
          <div className="mt-6">
            {data.status === "REVOKED" && (
              <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800" data-testid="consent-regrant-note">
                Svoj súhlas ste predtým odvolali. Ak ste si to rozmysleli, môžete ho nižšie znova udeliť.
              </div>
            )}
            <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-checkbox-label">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                className="mt-1 h-5 w-5 accent-blue-700" data-testid="consent-checkbox" />
              <span className="text-sm text-slate-700">Súhlasím so spracovaním mojich osobných údajov v uvedenom rozsahu a na uvedený účel.</span>
            </label>
            <button data-testid="consent-grant-btn" onClick={grant} disabled={!checked || busy}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-3.5 font-semibold transition-colors active:scale-95 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />} Udeliť súhlas
            </button>
          </div>
        )}

        {canRevoke && (
          <div className="mt-6">
            <p className="text-sm text-slate-500">Svoj súhlas môžete kedykoľvek odvolať — rovnako jednoducho ako ste ho udelili.</p>
            <button data-testid="consent-revoke-btn" onClick={revoke} disabled={busy}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-full px-8 py-3.5 font-semibold transition-colors active:scale-95 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <XCircle size={18} />} Odvolať súhlas
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center gap-1.5 text-xs text-slate-400 justify-center">
          <ShieldCheck size={13} /> Ochrana osobných údajov · Profeeluj
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="mx-auto max-w-md px-6 py-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-700 flex items-center justify-center"><Sparkles className="text-white" size={16} /></div>
          <span className="font-head font-bold tracking-tight">Profeeluj<span className="text-blue-700"> AI</span></span>
        </div>
      </header>
      <div className="mx-auto max-w-md px-5 py-6">{children}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
